<script setup lang="ts">
import { computed } from 'vue';
import { ChevronRight, Loader2, FolderOpen, FolderRoot } from 'lucide-vue-next';
import FileIcon from '@/components/FileIcon.vue';
import type { FileNode } from '@/composables/use-sftp';
import { useVirtualList, type SelectionState } from '@/composables/use-virtual-list';
import RootPicker from '@/components/RootPicker.vue';
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
} from '@/components/ui/context-menu';

type FlatEntry = {
	key: string;
	depth: number;
	expanded?: boolean;
	kind: 'directory' | 'file';
	isRoot: boolean;
	node: FileNode;
};

const props = defineProps<{
	rootNode: FileNode;
	rootPath: string;
	activeFilePath?: string | null;
	selectionState?: SelectionState;
}>();

const emit = defineEmits<{
	toggle: [node: FileNode];
	open: [node: FileNode];
	'change-root': [path: string];
}>();

// --- constants ---

const ROW_HEIGHT = 30;

const ROW_CLASSES =
	'flex w-full items-center gap-2 overflow-hidden rounded-md text-left text-sm outline-hidden hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0';

const CURSOR_CLASSES =
	'relative after:pointer-events-none after:absolute after:inset-[3px] after:rounded-sm after:border after:border-dotted after:border-sidebar-foreground/50';

// --- flat list ---

const flatNodes = computed<FlatEntry[]>(() => {
	const result: FlatEntry[] = [];
	const root = props.rootNode;

	// root node at depth 0
	result.push({
		key: root.path,
		depth: 0,
		expanded: root.isDirectory ? root.expanded : undefined,
		kind: root.isDirectory ? 'directory' : 'file',
		isRoot: true,
		node: root,
	});

	// walk children at depth 1+
	if (root.isDirectory && root.expanded && root.children && root.children.length > 0) {
		walk(root.children, 1);
	}

	function walk(nodes: FileNode[], depth: number) {
		for (const node of nodes) {
			const isDir = node.isDirectory;
			result.push({
				key: node.path,
				depth,
				expanded: isDir ? node.expanded : undefined,
				kind: isDir ? 'directory' : 'file',
				isRoot: false,
				node,
			});
			if (isDir && node.expanded && node.children && node.children.length > 0) {
				walk(node.children, depth + 1);
			}
		}
	}

	return result;
});

// --- virtual list ---

function onActivate(entry: FlatEntry) {
	if (entry.node.isDirectory) {
		emit('toggle', entry.node);
	} else {
		emit('open', entry.node);
	}
}

const list = useVirtualList<FlatEntry>({
	items: flatNodes,
	rowHeight: ROW_HEIGHT,
	selectionState: props.selectionState,
	onActivate,
	onExpand: (entry) => emit('toggle', entry.node),
	onCollapse: (entry) => emit('toggle', entry.node),
});

// --- context menu ---

function onNodeContextmenu(node: FileNode, index: number) {
	if (!list.isSelected(node.path)) {
		list.selectOnly(index);
	}
}

function onContextOpen() {
	for (const item of list.selectedItems()) {
		onActivate(item);
	}
}

// true when the cursor is on a directory (controls "Set as Root" visibility)
const cursorIsDirectory = computed(() => {
	const idx = list.cursorIdx.value;
	if (idx < 0 || idx >= flatNodes.value.length) return false;
	return flatNodes.value[idx]!.kind === 'directory';
});

function onContextSetRoot() {
	const idx = list.cursorIdx.value;
	if (idx < 0 || idx >= flatNodes.value.length) return;
	const entry = flatNodes.value[idx]!;
	if (entry.kind === 'directory') {
		emit('change-root', entry.node.path);
	}
}

function onMiddleClick(node: FileNode) {
	if (node.isDirectory) {
		emit('change-root', node.path);
	}
}

function onTriggerFocusin(event: FocusEvent) {
	if (event.target !== list.containerRef.value) {
		list.containerRef.value?.focus();
	}
}

defineExpose({
	focus: () => list.containerRef.value?.focus(),
});
</script>

<template>
	<ContextMenu>
		<ContextMenuTrigger as="div" @focusin="onTriggerFocusin">
			<div
				:ref="
					(el) => {
						list.containerRef.value = el as HTMLElement | null;
					}
				"
				tabindex="0"
				class="outline-none"
			>
				<!-- top spacer -->
				<div
					v-if="list.topPad.value > 0"
					:style="{ height: `${list.topPad.value}px` }"
					aria-hidden="true"
				/>

				<template v-for="entry in list.visibleEntries.value" :key="entry.key">
					<!-- directory row -->
					<div v-if="entry.kind === 'directory'" class="relative">
						<button
							:class="[
								ROW_CLASSES,
								'cursor-pointer',
								entry.isRoot && 'font-medium',
								list.isCursor(entry.index) && CURSOR_CLASSES,
								list.isSelected(entry.key) && list.selectionRadius(entry.index),
							]"
							:style="{ paddingLeft: `${8 + entry.depth * 12}px`, height: `${ROW_HEIGHT}px` }"
							:data-active="list.isSelected(entry.key)"
							:title="entry.node.symlinkTarget ?? undefined"
							:data-path="entry.node.path"
							@mousedown.left="list.onMousedown($event, entry.index)"
							@mousedown.middle.prevent="onMiddleClick(entry.node)"
							@contextmenu="onNodeContextmenu(entry.node, entry.index)"
						>
							<Loader2 v-if="entry.node.loading" class="shrink-0 animate-spin" />
							<ChevronRight
								v-else
								class="shrink-0 cursor-pointer transition-transform duration-200"
								:class="{ 'rotate-90': entry.node.expanded }"
								@mousedown.left.stop="emit('toggle', entry.node)"
							/>
							<FileIcon
								:name="entry.node.name"
								folder
								:open="entry.node.expanded"
								:symlink="entry.node.isSymlink"
								class="size-4 shrink-0"
							/>
							<span class="truncate">{{ entry.node.name }}</span>
						</button>
						<RootPicker
							v-if="entry.isRoot"
							:root-path="rootPath"
							@change-root="(p: string) => emit('change-root', p)"
						/>
					</div>

					<!-- file row -->
					<div v-else class="relative">
						<button
							:class="[
								ROW_CLASSES,
								'cursor-pointer',
								entry.isRoot && 'font-medium',
								list.isCursor(entry.index) && CURSOR_CLASSES,
								list.isSelected(entry.key) && list.selectionRadius(entry.index),
							]"
							:style="{ paddingLeft: `${8 + entry.depth * 12 + 24}px`, height: `${ROW_HEIGHT}px` }"
							:data-active="list.isSelected(entry.key)"
							:title="entry.node.symlinkTarget ?? undefined"
							:data-path="entry.node.path"
							@mousedown.left="list.onMousedown($event, entry.index)"
							@contextmenu="onNodeContextmenu(entry.node, entry.index)"
						>
							<FileIcon
								:name="entry.node.name"
								:symlink="entry.node.isSymlink"
								class="size-4 shrink-0"
							/>
							<span class="truncate">{{ entry.node.name }}</span>
						</button>
					</div>
				</template>

				<!-- bottom spacer -->
				<div
					v-if="list.bottomPad.value > 0"
					:style="{ height: `${list.bottomPad.value}px` }"
					aria-hidden="true"
				/>
			</div>
		</ContextMenuTrigger>

		<ContextMenuContent>
			<ContextMenuItem @select="onContextOpen">
				<FolderOpen class="mr-2" />
				Open
			</ContextMenuItem>
			<ContextMenuItem v-if="cursorIsDirectory" @select="onContextSetRoot">
				<FolderRoot class="mr-2" />
				Set as Root
			</ContextMenuItem>
		</ContextMenuContent>
	</ContextMenu>
</template>
